import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  FolderOpen,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const PatientCustomFieldGroupsTab = ({ t }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    display_order: 0,
    status: 'active'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patient-custom-fields/groups`);
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error(t?.('customFields.errorLoading') || 'Error loading groups');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (group = null) => {
    if (group) {
      setSelectedGroup(group);
      setFormData({
        name: group.name || '',
        description: group.description || '',
        display_order: group.display_order || 0,
        status: group.status || 'active'
      });
    } else {
      setSelectedGroup(null);
      setFormData({
        name: '',
        description: '',
        display_order: groups.length,
        status: 'active'
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedGroup) {
        await axios.put(`${API_URL}/api/patient-custom-fields/groups/${selectedGroup.id}`, formData);
        toast.success(t?.('customFields.groupUpdated') || 'Group updated successfully');
      } else {
        await axios.post(`${API_URL}/api/patient-custom-fields/groups`, formData);
        toast.success(t?.('customFields.groupCreated') || 'Group created successfully');
      }
      fetchGroups();
      setIsFormOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error saving group';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(t?.('customFields.confirmDeleteGroup') || `Are you sure you want to delete "${group.name}"? This will also delete all fields in this group.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/patient-custom-fields/groups/${group.id}`);
      toast.success(t?.('customFields.groupDeleted') || 'Group deleted successfully');
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting group');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-muted-foreground">
          {t?.('customFields.groupsDescription') || 'Organize custom fields into groups (e.g., Medical History, Family Info)'}
        </p>
        <Button onClick={() => handleOpenForm()} className="bg-primary hover:bg-primary/90">
          <Plus className="h-4 w-4 mr-2" />
          {t?.('customFields.newGroup') || 'New Group'}
        </Button>
      </div>

      {groups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {t?.('customFields.noGroups') || 'No groups created yet. Create your first group to organize custom fields.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {groups.map((group) => (
            <Card key={group.id} className="hover:shadow-md transition-shadow">
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <GripVertical className="h-4 w-4" />
                    <span className="text-sm font-medium">#{group.display_order + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{group.name}</h3>
                    {group.description && (
                      <p className="text-sm text-muted-foreground">{group.description}</p>
                    )}
                  </div>
                  <Badge variant={group.status === 'active' ? 'default' : 'secondary'}>
                    {group.status === 'active' ? (t?.('common:active') || 'Active') : (t?.('common:inactive') || 'Inactive')}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleOpenForm(group)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(group)} className="text-destructive hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedGroup ? (t?.('customFields.editGroup') || 'Edit Group') : (t?.('customFields.newGroup') || 'New Group')}
            </DialogTitle>
            <DialogDescription>
              {t?.('customFields.groupFormDescription') || 'Create a group to organize related custom fields together.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t?.('customFields.groupName') || 'Group Name'} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t?.('customFields.groupNamePlaceholder') || 'e.g., Medical History'}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t?.('customFields.description') || 'Description'}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t?.('customFields.descriptionPlaceholder') || 'Optional description for this group'}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t?.('customFields.displayOrder') || 'Display Order'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t?.('common:status') || 'Status'}</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t?.('common:active') || 'Active'}</SelectItem>
                    <SelectItem value="inactive">{t?.('common:inactive') || 'Inactive'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                {t?.('common:cancel') || 'Cancel'}
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={saving || !formData.name}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {selectedGroup ? (t?.('common:update') || 'Update') : (t?.('common:create') || 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientCustomFieldGroupsTab;
